import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useQueryParams from 'hooks/useQueryParams';
import FourDotLoader from 'components/FourDotLoader';
import api from 'api';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import EmailField from 'components/EmailField';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PasswordField from 'components/PasswordField';
import useCountdown from 'router/subrouters/Auth/hooks/useCountdown';
import { SUCCESS_MESSAGES } from 'request-responses';
import AgreeToTcField from '../AgreeToTcField';
import { ShareableLinkSecurityInfo } from 'models/ShareableLink';
import validations from 'validations';
import AuthContext from 'providers/Auth/Auth.context';
import { checkIsValidEmail, getShareableLinkRoute } from 'utils';
import shareableLinkService from 'services/shareableLinkService';
import CardWrapper from '../CardWrapper';

import './ShareableLinkUserForm.styles.scss';
import './ShareableLinkUserForm.styles.responsive.scss';

type ShareableLinkUserFormValues = {
  firstName: string;
  email: string;
  password: string;
  passcode?: string;
  agreeToTC?: boolean;
};

type ShareableLinkUserFormProps = {
  className?: string;
  shareableLinkId: string;
};

const ShareableLinkUserForm: React.FC<ShareableLinkUserFormProps> = (props) => {
  const { className, shareableLinkId } = props;

  const [isLoadingForgotPassword, setIsLoadingForgotPassword] = useState(false);
  const [isLoadingRequestPassword, setIsLoadingRequestPassword] =
    useState(false);

  const [hasTriedToFetchEmail, setHasTriedToFetchEmail] = useState(false);
  const [email, setEmail] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [securityInfo, setSecurityInfo] = useState<ShareableLinkSecurityInfo>();

  const { timeLeft, startCountdown } = useCountdown();

  const { popSuccess, popError, popServerError } = useContext(
    PopNotificationsContext,
  );
  const { onLoginReq } = useContext(AuthContext);

  const {
    params: { emailToken },
  } = useQueryParams();

  const shouldFetchEmail = useMemo(
    () => emailToken && !email && !hasTriedToFetchEmail,
    [email, emailToken, hasTriedToFetchEmail],
  );

  const initialValues = useMemo(() => ({ email }), [email]);

  const classes = classNames('shareable-link-user-form', className);

  const commonValidations = useValidations();

  const { t } = useTranslation();

  const onRequestPassword = useCallback(
    async (email: string) => {
      if (!checkIsValidEmail(email)) {
        popError(t('pleaseEnterValidEmailFirst'));
        return;
      }

      try {
        setIsLoadingRequestPassword(true);
        await api.shareableLinks.requestPassword(shareableLinkId, email);
        popSuccess(t('yourPasswordRequestubmittedSuccessfully'));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingRequestPassword(false);
      }
    },
    [popError, t, shareableLinkId, popSuccess, popServerError],
  );

  const onForgotPassword = useCallback(
    async (email: string) => {
      if (!checkIsValidEmail(email)) {
        popError(t('pleaseEnterValidEmailFirst'));
        return;
      }

      try {
        setIsLoadingForgotPassword(true);
        await api.auth.forgotPassword({ email }, shareableLinkId);
        startCountdown();
        popSuccess(t(SUCCESS_MESSAGES.FORGOT_PASSWORD));
      } catch (er) {
        popServerError(er);
      } finally {
        setIsLoadingForgotPassword(false);
      }
    },
    [popError, popServerError, popSuccess, shareableLinkId, startCountdown, t],
  );

  const onSubmit = useCallback(
    async (values: ShareableLinkUserFormValues) => {
      const { email, firstName, password, passcode, agreeToTC } = values;

      try {
        const { data: authBody } = await api.shareableLinks.authorization(
          shareableLinkId,
          firstName,
          email,
          password,
          passcode,
          agreeToTC,
        );

        if (securityInfo?.hasPassword)
          shareableLinkService.confirmedPasscodeSecureLinkId = shareableLinkId;

        onLoginReq(authBody, getShareableLinkRoute(shareableLinkId));
      } catch (e) {
        popServerError(e);
      }
    },
    [onLoginReq, popServerError, securityInfo?.hasPassword, shareableLinkId],
  );

  useEffect(() => {
    if (!shouldFetchEmail) return;

    (async () => {
      try {
        const {
          data: { email },
        } = await api.shareableLinks.getEmail(shareableLinkId, emailToken);
        setEmail(email);
      } catch {
        return;
      } finally {
        setHasTriedToFetchEmail(true);
      }
    })();
  }, [emailToken, shareableLinkId, shouldFetchEmail]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.shareableLinks.getSecurityInfo(
          shareableLinkId,
        );
        setSecurityInfo(data);
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [emailToken, shareableLinkId, shouldFetchEmail]);

  if (isLoading || shouldFetchEmail) return <FourDotLoader />;

  return (
    <div className={classes}>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({ submitting, values: { email }, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <CardWrapper
              title={t('accessSharedFiles')}
              hasNDA={securityInfo?.hasNDA}
            >
              <Field
                name="firstName"
                component={InputField}
                label={t('firstName')}
                required
                validate={commonValidations.name}
              />

              <EmailField required />

              <div className="password-wrapper">
                <p>{t('accountPassword')}</p>
                <p>{t('passwordSecureLink')}</p>
                <Field
                  name="password"
                  component={PasswordField}
                  required
                  validate={commonValidations.password}
                  withoutStrengthIndicator
                />
                <Button
                  variant="link"
                  disabled={isLoadingForgotPassword || timeLeft > 0}
                  data-loading={isLoadingForgotPassword}
                  onClick={() => onForgotPassword(email)}
                >
                  {timeLeft === 0
                    ? t('resetPassword')
                    : t('waitFor', { count: timeLeft })}
                </Button>
              </div>

              {securityInfo?.hasPassword && (
                <div className="password-wrapper">
                  <p>{t('accessPassword')}</p>
                  <p>{t('accessPasswordSecureLink')}</p>
                  <Field
                    name="passcode"
                    component={PasswordField}
                    required
                    validate={validations.required()}
                    withoutStrengthIndicator
                  />
                  <Button
                    variant="link"
                    disabled={isLoadingRequestPassword}
                    onClick={() => onRequestPassword(email)}
                  >
                    {t('requestAccessPassword')}
                  </Button>
                </div>
              )}

              {securityInfo?.hasTermsAndConditions && (
                <AgreeToTcField
                  termsAndConditionsContent={
                    securityInfo?.termsAndConditionsContent
                  }
                />
              )}

              <Button type="submit" disabled={submitting}>
                {t('continue')}
              </Button>
            </CardWrapper>
          </form>
        )}
      />
    </div>
  );
};

export default ShareableLinkUserForm;
